/**
 * Notification Item Component
 * Displays individual notification with action buttons
 */

import React from 'react';
import { 
  CheckIcon, 
  XMarkIcon,
  DocumentTextIcon,
  LightBulbIcon,
  HeartIcon,
  CogIcon,
  MapPinIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { useNotifications } from '../contexts/NotificationContext';

interface NotificationItemProps {
  notification: {
    notification_id: string;
    user_id: string;
    type: string;
    title: string;
    message: string;
    link: string;
    read: boolean;
    created_at: string;
    metadata: Record<string, any>;
  };
  onNavigate?: (link: string) => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onNavigate }) => {
  const { markAsRead, deleteNotification } = useNotifications();

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'qbr_completed':
        return <DocumentTextIcon className="h-5 w-5 text-blue-500" />;
      case 'insight_generated':
        return <LightBulbIcon className="h-5 w-5 text-yellow-500" />;
      case 'health_change':
        return <HeartIcon className="h-5 w-5 text-red-500" />;
      case 'integration_status':
        return <CogIcon className="h-5 w-5 text-green-500" />;
      case 'mapping_required':
        return <MapPinIcon className="h-5 w-5 text-orange-500" />;
      case 'team_invitation':
        return <UserGroupIcon className="h-5 w-5 text-purple-500" />;
      default:
        return <DocumentTextIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'qbr_completed':
        return 'border-l-blue-500 bg-blue-50 dark:bg-blue-900/20';
      case 'insight_generated':
        return 'border-l-yellow-500 bg-yellow-50 dark:bg-yellow-900/20';
      case 'health_change':
        return 'border-l-red-500 bg-red-50 dark:bg-red-900/20';
      case 'integration_status':
        return 'border-l-green-500 bg-green-50 dark:bg-green-900/20';
      case 'mapping_required':
        return 'border-l-orange-500 bg-orange-50 dark:bg-orange-900/20';
      case 'team_invitation':
        return 'border-l-purple-500 bg-purple-50 dark:bg-purple-900/20';
      default:
        return 'border-l-gray-500 bg-gray-50 dark:bg-gray-900/20';
    }
  };

  const handleClick = async () => {
    if (!notification.read) {
      await markAsRead(notification.notification_id);
    }
    
    if (onNavigate && notification.link) {
      onNavigate(notification.link);
    }
  };

  const handleMarkAsRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!notification.read) {
      await markAsRead(notification.notification_id);
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await deleteNotification(notification.notification_id);
  };

  const formatTime = (createdAt: string) => {
    try {
      const date = new Date(createdAt);
      const now = new Date();
      const diffMs = date.getTime() - now.getTime();
      const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

      const divisions: { amount: number; unit: Intl.RelativeTimeFormatUnit }[] = [
        { amount: 60, unit: 'second' },
        { amount: 60, unit: 'minute' },
        { amount: 24, unit: 'hour' },
        { amount: 7, unit: 'day' },
        { amount: 4.34524, unit: 'week' },
        { amount: 12, unit: 'month' },
        { amount: Number.POSITIVE_INFINITY, unit: 'year' },
      ];

      let duration = Math.round(diffMs / 1000);
      for (let i = 0; i <= divisions.length; i++) {
        const division = divisions[i];
        if (Math.abs(duration) < division.amount) {
          return rtf.format(duration, division.unit);
        }
        duration = Math.round(duration / division.amount);
      }
      return 'just now';
    } catch {
      return 'Recently';
    }
  };

  return (
    <div
      className={`
        relative p-4 border-l-4 transition-all duration-200 hover:shadow-md cursor-pointer
        ${getNotificationColor(notification.type)}
        ${notification.read 
          ? 'opacity-75 hover:opacity-100' 
          : 'opacity-100'
        }
      `}
      onClick={handleClick}
    >
      {/* Unread indicator */}
      {!notification.read && (
        <div className="absolute top-2 right-2 w-2 h-2 bg-blue-500 rounded-full"></div>
      )}

      <div className="flex items-start space-x-3">
        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5">
          {getNotificationIcon(notification.type)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h4 className={`text-sm font-medium ${notification.read ? 'text-gray-600 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}>
                {notification.title}
              </h4>
              <p className={`text-sm mt-1 ${notification.read ? 'text-gray-500 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}>
                {notification.message}
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-600 mt-2">
                {formatTime(notification.created_at)}
              </p>
            </div>

            {/* Action buttons */}
            <div className="flex items-center space-x-1 ml-2">
              {!notification.read && (
                <button
                  onClick={handleMarkAsRead}
                  className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                  title="Mark as read"
                >
                  <CheckIcon className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={handleDelete}
                className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                title="Delete notification"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Metadata display for certain notification types */}
      {notification.metadata && Object.keys(notification.metadata).length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-600">
            {notification.type === 'qbr_completed' && notification.metadata.customer_name && (
              <span>Account: {notification.metadata.customer_name}</span>
            )}
            {notification.type === 'insight_generated' && notification.metadata.customer_name && (
              <span>Account: {notification.metadata.customer_name}</span>
            )}
            {notification.type === 'health_change' && notification.metadata.customer_name && (
              <span>Account: {notification.metadata.customer_name}</span>
            )}
            {notification.type === 'integration_status' && notification.metadata.integration_type && (
              <span>Integration: {notification.metadata.integration_type}</span>
            )}
            {notification.type === 'mapping_required' && notification.metadata.source_type && (
              <span>Source: {notification.metadata.source_type}</span>
            )}
            {notification.type === 'team_invitation' && notification.metadata.email && (
              <span>Email: {notification.metadata.email}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationItem;
